import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setCsrfToken } from "../api/client";
import type { MeResponse } from "../api/types";
import { api } from "../api/client";

interface AuthState {
  loading: boolean;
  authenticated: boolean;
  username: string | null;
  remember: boolean;
  expiresTs: number | null;
}

interface AuthContextValue extends AuthState {
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  afterLogin: (payload: { username: string; csrf_token: string; remember: boolean; expires_ts: number }) => void;
}

const initial: AuthState = {
  loading: true,
  authenticated: false,
  username: null,
  remember: false,
  expiresTs: null,
};

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(initial);

  const refresh = useCallback(async () => {
    try {
      const me = await api<MeResponse>("/auth/me");
      if (me.authenticated && me.csrf_token) {
        setCsrfToken(me.csrf_token);
        setState({
          loading: false,
          authenticated: true,
          username: me.username ?? "admin",
          remember: me.remember ?? false,
          expiresTs: me.expires_ts ?? null,
        });
      } else {
        setCsrfToken(null);
        setState({ loading: false, authenticated: false, username: null, remember: false, expiresTs: null });
      }
    } catch {
      setCsrfToken(null);
      setState({ loading: false, authenticated: false, username: null, remember: false, expiresTs: null });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api<void>("/auth/logout", { method: "POST" });
    } finally {
      setCsrfToken(null);
      setState({ loading: false, authenticated: false, username: null, remember: false, expiresTs: null });
    }
  }, []);

  const afterLogin = useCallback(
    (payload: { username: string; csrf_token: string; remember: boolean; expires_ts: number }) => {
      setCsrfToken(payload.csrf_token);
      setState({
        loading: false,
        authenticated: true,
        username: payload.username,
        remember: payload.remember,
        expiresTs: payload.expires_ts,
      });
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh, signOut, afterLogin }),
    [state, refresh, signOut, afterLogin],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within <AuthProvider>");
  return v;
}
